import React from "react";
import { Box, SvgIcon, type SvgIconProps } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CallIcon from "@mui/icons-material/Call";
import { CALLACTIONUMBER, WhatsAppACTIONUMBER } from "./global_varibles";
import { styled, alpha } from "@mui/material/styles";

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/place/MuftyKare+DryClean%7C+Laundry+%7CPickup+%26+Delivery+%7C+Vizag/@17.7376629,83.3060535,17z/data=!3m1!4b1!4m6!3m5!1s0x3a394332f9651fe3:0xb5df1e30e5ee8a3f!8m2!3d17.7376629!4d83.3060535!16s%2Fg%2F11bw82m834?entry=ttu&g_ep=EgoyMDI1MTExMi4wIKXMDSoASAFQAw%3D%3D";

interface CallActionButtonProps {
  phone_number: string;
}
 
const FloatingButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.75),
  borderRadius: "50%",
  color: "white",
  cursor: "pointer",
  border: `1px solid ${alpha("#fff", 0.12)}`,
  backdropFilter: "blur(10px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.25)",
  "&:hover": {
    transform: "translateX(-6px) scale(1.08)",
    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.35)",
  },
  "&:active": {
    transform: "translateX(-4px) scale(1.04)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
  },
}));

export function CallActionButton({ phone_number }: CallActionButtonProps): React.ReactElement {
  const handleCallClick = () => {
    window.open(`tel:${phone_number}`, "_blank");
  };
  return (
    <FloatingButton
      onClick={handleCallClick}
      sx={{
        background: `linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)`,
        "&:hover": {
          borderColor: alpha("#3b82f6", 0.45),
          background: `linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)`,
        },
      }}
    >
      <CallIcon sx={{ fontSize: { sm: 40, xs: 30 } }} />
    </FloatingButton>
  );
}

interface WhatsappActionButtonProps {
  phoneNum: string;
  message: string;
}

export function WhatsappActionButton({ phoneNum, message }: WhatsappActionButtonProps): React.ReactElement {
  const handleWhatsAppClick = () => {
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNum}?text=${encoded}`, "_blank");
  };
  return (
    <FloatingButton
      onClick={handleWhatsAppClick}
      sx={{
        background: `linear-gradient(135deg, #10b981 0%, #059669 100%)`,
        "&:hover": {
          borderColor: alpha("#10b981", 0.45),
          background: `linear-gradient(135deg, #059669 0%, #047857 100%)`,
        },
      }}
    >
      <WhatsAppIcon sx={{ fontSize: { sm: 40, xs: 30 } }} />
    </FloatingButton>
  );
}

// Real Google Maps pin icon
function GoogleMapsIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
        fill="#EA4335"
      />
      <path
        d="M12 2c-1.93 0-3.68.78-4.95 2.05L12 9l4.95-4.95C15.68 2.78 13.93 2 12 2z"
        fill="#4285F4"
      />
      <path
        d="M7.05 4.05C5.78 5.32 5 7.07 5 9c0 1.93.78 3.68 2.05 4.95L12 9 7.05 4.05z"
        fill="#34A853"
      />
      <path
        d="M16.95 4.05L12 9l4.95 4.95C18.22 12.68 19 10.93 19 9c0-1.93-.78-3.68-2.05-4.95z"
        fill="#FBBC05"
      />
      <circle cx="12" cy="9" r="3" fill="white" />
    </SvgIcon>
  );
}

interface MapsActionButtonProps {
  url: string;
}

export function MapsActionButton({ url }: MapsActionButtonProps): React.ReactElement {
  const handleMapsClick = () => {
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <FloatingButton
      onClick={handleMapsClick}
      sx={{
        background: "#ffffff",                 // white background
        border: `1px solid ${alpha("#000", 0.06)}`, // subtle border for separation
        boxShadow: "0 8px 20px rgba(2,6,23,0.16)",  // subtle shadow
        "&:hover": {
          background: "#f6f6f6",
          transform: "translateX(-6px) scale(1.08)",
        },
      }}
      aria-label="Open location in Google Maps"
      title="Open location in Google Maps"
    >
      <GoogleMapsIcon
        sx={{
          // match size to the other icons, keep SVG colors intact
          fontSize: { sm: 40, xs: 30 },
          width: { sm: 40, xs: 30 },
          height: { sm: 40, xs: 30 },
          display: "block",
        }}
      />
    </FloatingButton>
  );
}

export function FloatingActionButton(): React.ReactElement | null {
  try {
    const message =
      "Hi, I would like to schedule an appointment. Could you provide available time slots?";
    return (
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          right: 16,
          transform: "translateY(-50%)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          zIndex: 1000,
        }}
      >
        <WhatsappActionButton phoneNum={WhatsAppACTIONUMBER} message={message} />
        <CallActionButton phone_number={CALLACTIONUMBER} />
        <MapsActionButton url={GOOGLE_MAPS_URL} />
      </Box>
    );
  } catch (error) {
    console.log("error in floating action button", error);
    return null;
  }
}
